import { z } from 'zod';
import { zTranslatable } from './translatable.js';
const block = z.object({ title: zTranslatable, body: zTranslatable });
/** Admin "save service page". Content may be empty while `draft`; `published` needs the essentials in both languages. */
export const servicePageSaveSchema = z
    .object({
    status: z.enum(['draft', 'published']),
    h1: zTranslatable,
    metaTitle: zTranslatable,
    metaDescription: zTranslatable,
    heroLead: zTranslatable,
    answer: zTranslatable,
    included: z.array(block),
    who: z.array(zTranslatable),
    notThis: zTranslatable.nullable(),
    steps: z.array(block),
    proof: z.object({ quote: zTranslatable, who: zTranslatable }).nullable(),
    faq: z.array(z.object({ q: zTranslatable, a: zTranslatable })),
    related: z.array(z.string().max(60)).max(6),
})
    .superRefine((v, ctx) => {
    if (v.status !== 'published')
        return;
    for (const [field, tr] of [
        ['h1', v.h1],
        ['metaTitle', v.metaTitle],
        ['metaDescription', v.metaDescription],
        ['heroLead', v.heroLead],
        ['answer', v.answer],
    ]) {
        if (!tr.en.trim() || !tr.bg.trim()) {
            ctx.addIssue({ code: 'custom', path: [field], message: 'Required in both languages to publish' });
        }
    }
});
/** Admin "save role page". Publishing needs the essentials in both languages. */
export const sectorPageSaveSchema = z
    .object({
    status: z.enum(['draft', 'published']),
    h1: zTranslatable,
    metaTitle: zTranslatable,
    metaDescription: zTranslatable,
    short: zTranslatable,
    answer: zTranslatable,
    titles: z.array(zTranslatable),
    filled: z.array(zTranslatable),
    hard: z.array(zTranslatable),
    how: z.array(zTranslatable),
    faq: z.array(z.object({ q: zTranslatable, a: zTranslatable })),
})
    .superRefine((v, ctx) => {
    if (v.status !== 'published')
        return;
    for (const [field, tr] of [
        ['h1', v.h1],
        ['metaTitle', v.metaTitle],
        ['metaDescription', v.metaDescription],
        ['short', v.short],
        ['answer', v.answer],
    ]) {
        if (!tr.en.trim() || !tr.bg.trim()) {
            ctx.addIssue({ code: 'custom', path: [field], message: 'Required in both languages to publish' });
        }
    }
    if (v.titles.length === 0)
        ctx.addIssue({ code: 'custom', path: ['titles'], message: 'Add the job titles you recruit for' });
    if (v.hard.length < 3)
        ctx.addIssue({ code: 'custom', path: ['hard'], message: 'Add three to five specific sentences on what makes hiring in this sector hard' });
});
export const faqSaveSchema = z.object({
    group: z.enum(['employers', 'candidates', 'about']),
    order: z.number().int().min(0).max(999),
    q: z.object({ en: z.string().min(1), bg: z.string().min(1) }),
    a: z.object({ en: z.string().min(1), bg: z.string().min(1) }),
    onHome: z.boolean(),
    status: z.enum(['draft', 'published']),
});
export const siteFactsSchema = z.object({
    founded: z.string().max(10),
    stats: z.array(z.object({ n: zTranslatable, l: zTranslatable })).max(6),
    clients: z.array(z.string().min(1).max(80)).max(16),
    rating: z.object({ value: z.string().min(1).max(5), count: z.string().max(8).default('') }).nullable(),
    legal: z.object({
        registeredName: z.string().max(120),
        eik: z.string().max(40),
        vat: z.string().max(40).default(''),
        dataController: z.string().max(200),
    }),
});
/** Admin "save insight". Publishing needs the essentials in at least one locale pair (slug + title + answer + body). */
export const insightSaveSchema = z
    .object({
    slug: z.object({ en: z.string().max(120), bg: z.string().max(120) }),
    title: zTranslatable,
    answer: zTranslatable,
    body: zTranslatable,
    topic: zTranslatable,
    authorName: z.string().max(120),
    readMinutes: z.number().int().min(1).max(60),
    status: z.enum(['draft', 'published']),
    publishedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'),
})
    .superRefine((v, ctx) => {
    const slugOk = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    let locales = 0;
    for (const l of ['en', 'bg']) {
        const hasContent = [v.slug[l], v.title[l], v.answer[l], v.body[l]].some((x) => x.trim());
        if (!hasContent)
            continue;
        locales++;
        if (!slugOk.test(v.slug[l])) {
            ctx.addIssue({
                code: 'custom',
                path: ['slug', l],
                message: 'Slug required: lowercase Latin letters, digits and dashes',
            });
        }
        if (v.status === 'published') {
            for (const f of ['title', 'answer', 'body']) {
                if (!v[f][l].trim()) {
                    ctx.addIssue({ code: 'custom', path: [f, l], message: `Required to publish in ${l.toUpperCase()}` });
                }
            }
        }
    }
    if (locales === 0) {
        ctx.addIssue({ code: 'custom', path: ['title'], message: 'Write the article in at least one language' });
    }
});
//# sourceMappingURL=pages.js.map